import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { INSURANCE_TYPES } from "@/data/siteData";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors duration-200 focus:border-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-600/20";

export default function EnquiryForm({ defaultType = "", dark = false }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "", company: "", phone: "", email: "", city: "",
    insurance_type: defaultType, message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.insurance_type) {
      toast.error(t("form.selectType"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success(t("form.success"));
      setForm({ name: "", company: "", phone: "", email: "", city: "", insurance_type: defaultType, message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("form.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="enquiry-form" onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <label htmlFor="enq-name" className={`mb-1.5 block text-xs font-semibold uppercase tracking-wider ${dark ? "text-slate-300" : "text-slate-600"}`}>{t("form.name")}</label>
        <input id="enq-name" data-testid="enquiry-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={t("form.namePh")} className={inputCls} />
      </div>
      <div>
        <label htmlFor="enq-company" className={`mb-1.5 block text-xs font-semibold uppercase tracking-wider ${dark ? "text-slate-300" : "text-slate-600"}`}>{t("form.company")}</label>
        <input id="enq-company" data-testid="enquiry-company-input" value={form.company} onChange={set("company")} placeholder={t("form.companyPh")} className={inputCls} />
      </div>
      <div>
        <label htmlFor="enq-phone" className={`mb-1.5 block text-xs font-semibold uppercase tracking-wider ${dark ? "text-slate-300" : "text-slate-600"}`}>{t("form.phone")}</label>
        <input id="enq-phone" data-testid="enquiry-phone-input" required minLength={8} value={form.phone} onChange={set("phone")} placeholder={t("form.phonePh")} className={inputCls} />
      </div>
      <div>
        <label htmlFor="enq-email" className={`mb-1.5 block text-xs font-semibold uppercase tracking-wider ${dark ? "text-slate-300" : "text-slate-600"}`}>{t("form.email")}</label>
        <input id="enq-email" data-testid="enquiry-email-input" type="email" required value={form.email} onChange={set("email")} placeholder={t("form.emailPh")} className={inputCls} />
      </div>
      <div>
        <label htmlFor="enq-city" className={`mb-1.5 block text-xs font-semibold uppercase tracking-wider ${dark ? "text-slate-300" : "text-slate-600"}`}>{t("form.city")}</label>
        <input id="enq-city" data-testid="enquiry-city-input" value={form.city} onChange={set("city")} placeholder={t("form.cityPh")} className={inputCls} />
      </div>
      <div>
        <label htmlFor="enq-type" className={`mb-1.5 block text-xs font-semibold uppercase tracking-wider ${dark ? "text-slate-300" : "text-slate-600"}`}>{t("form.type")}</label>
        <select id="enq-type" data-testid="enquiry-type-select" required value={form.insurance_type} onChange={set("insurance_type")} className={inputCls}>
          <option value="" disabled>{t("form.typePh")}</option>
          {INSURANCE_TYPES.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="enq-message" className={`mb-1.5 block text-xs font-semibold uppercase tracking-wider ${dark ? "text-slate-300" : "text-slate-600"}`}>{t("form.message")}</label>
        <textarea id="enq-message" data-testid="enquiry-message-input" rows={4} value={form.message} onChange={set("message")} placeholder={t("form.messagePh")} className={`${inputCls} resize-none`} />
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          data-testid="enquiry-submit-button"
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 bg-amber-600 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-amber-700 disabled:opacity-60 sm:w-auto"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {loading ? t("form.submitting") : t("form.submit")}
        </button>
        <p className={`mt-3 text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>
          {t("form.consent")}
        </p>
      </div>
    </form>
  );
}
